import { motion } from 'framer-motion';
import { Target, ShieldCheck, UserCheck, Lock, TrendingUp } from 'lucide-react';

const NODES = [
  { id: 0, icon: Target,      label: 'Intent',      sub: 'Define outcome first',       color: '#074EDC', glow: 'rgba(7,78,220,0.6)' },
  { id: 1, icon: ShieldCheck, label: 'Evidence',    sub: 'Proof before confidence',    color: '#4AAC00', glow: 'rgba(74,172,0,0.6)' },
  { id: 2, icon: UserCheck,   label: 'HumAIn',      sub: 'Accountability stays human', color: '#FF8C00', glow: 'rgba(255,140,0,0.6)' },
  { id: 3, icon: Lock,        label: 'Guardrails',  sub: 'Boundaries on AI execution', color: '#ED0216', glow: 'rgba(237,2,22,0.6)' },
  { id: 4, icon: TrendingUp,  label: 'Improve',     sub: 'Learn from every outcome',   color: '#4AAC00', glow: 'rgba(74,172,0,0.6)' },
];

const CX = 250;
const CY = 250;
const RADIUS = 165;
const NODE_W = 104;
const NODE_H = 76;
const TOTAL = NODES.length;

function polarToXY(angleDeg: number, r: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
}

function arcPath(from: number, to: number, r: number) {
  const start = polarToXY((360 / TOTAL) * from, r);
  const end   = polarToXY((360 / TOTAL) * to,   r);
  const large = to - from > TOTAL / 2 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`;
}

export function GovernanceCycle() {
  return (
    <div className="relative flex items-center justify-center select-none w-full max-w-[500px] aspect-square">

      <svg viewBox="0 0 500 500" className="w-full h-full" style={{ overflow: 'visible' }}>
        <defs>
          {/* Per-arc color gradients */}
          {NODES.map((node, i) => (
            <linearGradient key={i} id={`arcGrad${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor={node.color} stopOpacity="0.9" />
              <stop offset="100%" stopColor={NODES[(i + 1) % TOTAL].color} stopOpacity="0.9" />
            </linearGradient>
          ))}


        </defs>

        {/* Connecting arcs — each segment uses its own gradient color */}
        {NODES.map((node, i) => (
          <motion.path
            key={i}
            d={arcPath(i, (i + 1) % TOTAL, RADIUS)}
            fill="none"
            stroke={`url(#arcGrad${i})`}
            strokeWidth={2.5}
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 + i * 0.18, ease: 'easeOut' }}
          />
        ))}

        {/* Rotating dashed ring */}
        <motion.circle
          cx={CX} cy={CY} r={RADIUS}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={1}
          strokeDasharray="5 9"
          animate={{ rotate: 360 }}
          transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
          style={{ transformOrigin: `${CX}px ${CY}px` }}
        />

        {/* Animated dots travelling around ring — each a brand color */}
        {NODES.map((node, i) => {
          const startAngle = (360 / TOTAL) * i;
          return (
            <motion.g
              key={i}
              style={{ transformOrigin: `${CX}px ${CY}px` }}
              animate={{ rotate: [startAngle, startAngle + 360] }}
              transition={{ duration: 9, delay: 0, repeat: Infinity, ease: 'linear' }}
            >
              <circle r={3.5} fill={node.color} cx={CX} cy={CY - RADIUS} />
            </motion.g>
          );
        })}

        {/* Centre multicolor ring accent */}
        <motion.circle
          cx={CX} cy={CY} r={56}
          fill="none"
          stroke="url(#arcGrad0)"
          strokeWidth={2}
          strokeDasharray="20 12"
          animate={{ rotate: -360 }}
          transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
          style={{ transformOrigin: `${CX}px ${CY}px` }}
        />
        <text x={CX} y={CY - 9} textAnchor="middle" style={{ fontSize: 15, fontWeight: 800, fontFamily: 'Inter, sans-serif', fill: '#ffffff' }}>TICCA</text>
        <text x={CX} y={CY + 11} textAnchor="middle" style={{ fontSize: 10, fontFamily: 'Inter, sans-serif', fill: 'rgba(255,255,255,0.5)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>Governance</text>
        <text x={CX} y={CY + 26} textAnchor="middle" style={{ fontSize: 10, fontFamily: 'Inter, sans-serif', fill: 'rgba(255,255,255,0.5)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>Cycle</text>

        {/* Nodes */}
        {NODES.map((node, i) => {
          const angle = (360 / TOTAL) * i;
          const { x, y } = polarToXY(angle, RADIUS);
          const Icon = node.icon;
          return (
            <motion.g
              key={node.id}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.15 + i * 0.16, ease: [0.34, 1.56, 0.64, 1] }}
              style={{ transformOrigin: `${x}px ${y}px` }}
            >
              {/* Node card — dark glass */}
              <foreignObject
                x={x - NODE_W / 2}
                y={y - NODE_H / 2}
                width={NODE_W}
                height={NODE_H}
              >
                <div
                  style={{
                    width: NODE_W,
                    height: NODE_H,
                    background: 'rgba(4,8,30,0.92)',
                    border: `1.5px solid ${node.color}55`,
                    borderRadius: 12,
                    borderTop: `2.5px solid ${node.color}`,
                    boxShadow: `0 4px 24px ${node.glow.replace('0.6', '0.22')}, inset 0 1px 0 rgba(255,255,255,0.06)`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    padding: '6px 8px',
                  }}
                >
                  <Icon size={17} color={node.color} />
                  <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#ffffff', fontFamily: 'Inter, sans-serif', textAlign: 'center', lineHeight: 1.2 }}>{node.label}</p>
                  <p style={{ margin: 0, fontSize: 9, color: 'rgba(255,255,255,0.55)', fontFamily: 'Inter, sans-serif', textAlign: 'center', lineHeight: 1.3 }}>{node.sub}</p>
                </div>
              </foreignObject>
            </motion.g>
          );
        })}


      </svg>
    </div>
  );
}
